// Cookie consent preference store (white-label).
// Persisted under localStorage["nb_cookie_consent_v1"].
// Backward-compatible with the previous boolean schema:
//   "granted" => analytics+marketing on, "denied" => both off.

export type ConsentCategories = {
  necessary: true; // always on
  analytics: boolean;
  marketing: boolean;
};

export type StoredConsent = {
  v: 1;
  ts: number;
  categories: ConsentCategories;
};

export const CONSENT_KEY = "nb_cookie_consent_v1";

export const DEFAULT_DENIED: ConsentCategories = {
  necessary: true,
  analytics: false,
  marketing: false,
};

export const ACCEPT_ALL: ConsentCategories = {
  necessary: true,
  analytics: true,
  marketing: true,
};

export function readConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    // Back-compat with legacy "granted" / "denied" string values
    if (raw === "granted") {
      return { v: 1, ts: Date.now(), categories: ACCEPT_ALL };
    }
    if (raw === "denied") {
      return { v: 1, ts: Date.now(), categories: DEFAULT_DENIED };
    }
    const parsed = JSON.parse(raw);
    if (parsed && parsed.categories) {
      return {
        v: 1,
        ts: parsed.ts ?? Date.now(),
        categories: {
          necessary: true,
          analytics: Boolean(parsed.categories.analytics),
          marketing: Boolean(parsed.categories.marketing),
        },
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function writeConsent(categories: ConsentCategories): StoredConsent {
  const payload: StoredConsent = {
    v: 1,
    ts: Date.now(),
    categories: { ...categories, necessary: true },
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
  } catch {}
  return payload;
}

export function clearConsent() {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {}
}

export function hasAnalyticsConsent(): boolean {
  return readConsent()?.categories.analytics === true;
}

export function hasMarketingConsent(): boolean {
  return readConsent()?.categories.marketing === true;
}
